import { useState } from 'react'
import { Field } from 'react-final-form'
import { IntegrationTypeValue } from '@phrasee/phrasee-typings/typings/project/project-configuration.types'
import ControlInput from 'workflow/common/controlInput'

import { TextareaType } from 'common/components/dynamicFields/interfaces'
import FormItem from 'common/components/FormItem'
import { stripForbiddenCharacters } from 'common/helpers/stripForbiddenCharacters'

import { TemplateInfoWithInternalId } from '../addedTemplates/AddedComponentList'
import { getExperimentPrefixedName } from '../helpers'

const countNumberOfBreakLines = (text: string) => {
  return text?.match(/\n|↵/g)?.length || 0
}

const DOUBLE_PERCENTAGE_ERROR = (
  <>
    You've included %%, please make sure your AmpScript is formatted correctly.{' '}
    <a
      href="https://help.salesforce.com/s/articleView?id=sf.mc_es_available_personalization_strings.htm&type=5"
      target="_blank"
      rel="noreferrer"
    >
      You can find available tags here.
    </a>
  </>
)

type Props = {
  template: TemplateInfoWithInternalId
  config: TextareaType
  integrationType?: IntegrationTypeValue
}

const ControlSubjectLine = ({ template, config, integrationType }: Props) => {
  const [shouldShowLineBreaksWarning, setShouldShowLineBreaksWarning] =
    useState(false)
  const minRows = config.min && config.min > 1 ? config.min : 1.334 // equals 38px

  const checkLineBreaksWarning = (
    originalValue: string,
    maxLineBreaks: number = 0
  ) => {
    if (shouldShowLineBreaksWarning) {
      const targetValue = stripForbiddenCharacters(originalValue)

      if (countNumberOfBreakLines(targetValue) <= maxLineBreaks) {
        setShouldShowLineBreaksWarning(false)
      }
    }
  }

  const validateHumanControl = (value: string | undefined) => {
    if (!value?.trim()) {
      return 'Enter a name for your experiment.'
    }
    if (shouldShowLineBreaksWarning) {
      return " Line breaks aren't allowed for this type of content. Please edit your copy."
    }
    if (value.includes('%%') && integrationType === 'sfmcv2') {
      return DOUBLE_PERCENTAGE_ERROR
    }
  }

  return (
    <Field<string>
      name={getExperimentPrefixedName(template, 'controlSubjectLine')}
      validate={validateHumanControl}
    >
      {({ input, meta }) => (
        <FormItem
          error={meta.touched ? meta.error : undefined}
          label={config.label}
          htmlFor={input.name}
          className="flex-none mb-4"
        >
          <ControlInput
            data-cy="control-subject-line-input"
            id={input.name}
            name={input.name}
            className="hover:border-orange-400 focus:border-orange-400 outline-none shadow-none pt-2"
            autoSize={{ minRows }}
            maxRows={config.max || 0}
            placeholder="Enter your control"
            onBlur={input.onBlur}
            onChange={(value, originalValue) => {
              checkLineBreaksWarning(originalValue, config.max)
              input.onChange(value)
            }}
            onLineBreaksWarning={() => setShouldShowLineBreaksWarning(true)}
            value={input.value}
          />
        </FormItem>
      )}
    </Field>
  )
}

export default ControlSubjectLine
